import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import DocuVaultAccessAlerts from './DocuVaultAccessAlerts';
import {
  getAccessLogs, clearAccessLogs,
  ACCESS_EVENT_TYPES,
} from '../../services/docuvaultService';

const {
  FiX, FiEye, FiDownload, FiUploadCloud, FiShield,
  FiTrash2, FiUsers, FiRefreshCw, FiList, FiSearch,
  FiActivity, FiSave, FiAlertTriangle,
} = FiIcons;

const EVENT_ICONS = {
  view: FiEye,
  download: FiDownload,
  upload: FiUploadCloud,
  authorize: FiShield,
  delete: FiTrash2,
  delegate: FiUsers,
  renew: FiRefreshCw,
};

function formatRelative(ts) {
  const diff = Date.now() - new Date(ts).getTime();
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return `${Math.floor(diff / 86400000)}d ago`;
}

function formatAbsolute(ts) {
  return new Date(ts).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// ── Single Timeline Entry ────────────────────────────────────────────────────
function LogEntry({ log, showDocName }) {
  const cfg = ACCESS_EVENT_TYPES[log.eventType] || { label: log.eventType, color: 'text-gray-500', bg: 'bg-gray-50' };
  const Icon = EVENT_ICONS[log.eventType] || FiActivity;

  const detailPairs = log.details
    ? Object.entries(log.details).filter(([k]) => k !== 'userAgent' && k !== 'docName')
    : [];

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 8 }}
      className="flex items-start gap-3 p-3 bg-white rounded-2xl border border-brand-dark hover:border-accent-navy/20 transition-all"
    >
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${cfg.bg}`}>
        <SafeIcon icon={Icon} className={`w-4 h-4 ${cfg.color}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
          <span className={`text-[9px] font-black uppercase tracking-widest ${cfg.color}`}>{cfg.label}</span>
          {showDocName && log.docName && (
            <span className="text-xs font-bold text-accent-navy truncate max-w-[200px]">{log.docName}</span>
          )}
        </div>
        <div className="flex items-center gap-2 text-[10px] text-text-muted">
          <span className="font-medium">{log.userName}</span>
          <span>·</span>
          <span title={formatAbsolute(log.timestamp)}>{formatRelative(log.timestamp)}</span>
        </div>
        {detailPairs.length > 0 && (
          <div className="mt-1 text-[9px] text-text-muted/70 font-mono truncate">
            {detailPairs.map(([k, v]) => `${k}: ${String(v).slice(0, 50)}`).join(' · ')}
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ── Main Modal ────────────────────────────────────────────────────────────────
const DocuVaultAccessLogModal = ({ isOpen, onClose, document: doc }) => {
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showConfirmClear, setShowConfirmClear] = useState(false);

  const loadLogs = () => {
    setLogs(getAccessLogs(doc?.id || null));
  };

  useEffect(() => {
    if (isOpen) {
      loadLogs();
      setFilter('all');
      setSearch('');
      setShowConfirmClear(false);
    }
  }, [isOpen, doc?.id]);

  const stats = useMemo(() => {
    const s = { total: logs.length, types: {} };
    logs.forEach(l => { s.types[l.eventType] = (s.types[l.eventType] || 0) + 1; });
    return s;
  }, [logs]);

  const filtered = useMemo(() => {
    let result = logs;
    if (filter !== 'all') result = result.filter(l => l.eventType === filter);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(l =>
        l.docName?.toLowerCase().includes(q) ||
        l.userName?.toLowerCase().includes(q) ||
        l.eventType?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [logs, filter, search]);

  const handleExport = () => {
    const headers = ['Timestamp', 'Event', 'Document', 'User', 'Details'];
    const rows = filtered.map(l => [
      l.timestamp, l.eventType, l.docName || '', l.userName || '',
      JSON.stringify(l.details || {}),
    ]);
    const csv = [headers, ...rows]
      .map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `docuvault-access-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleClear = () => {
    clearAccessLogs(doc?.id || null);
    loadLogs();
    setShowConfirmClear(false);
  };

  if (!isOpen) return null;

  const eventTypes = Object.keys(ACCESS_EVENT_TYPES);

  return (
    <div className="fixed inset-0 z-[210] flex items-center justify-center p-4 bg-accent-navy/70 backdrop-blur-md">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-[40px] shadow-2xl w-full max-w-2xl overflow-hidden border border-brand-dark flex flex-col max-h-[92vh]"
      >
        {/* Header */}
        <div className="p-6 bg-gradient-to-r from-accent-navy to-[#1a2a5e] flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 bg-accent-gold/20 text-accent-gold rounded-2xl flex items-center justify-center">
              <SafeIcon icon={FiList} className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-serif text-white">Access Log</h3>
              <p className="text-white/50 text-[10px] font-black uppercase tracking-widest truncate max-w-[300px]">
                {doc ? doc.original_name : 'All Documents'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-white/60 hover:text-white rounded-xl transition-colors">
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-4 divide-x divide-brand-light border-b border-brand-light flex-shrink-0">
          <div className="p-3 text-center">
            <p className="text-lg font-serif font-bold text-accent-navy">{stats.total}</p>
            <p className="text-[9px] font-black uppercase tracking-widest text-text-muted">Total</p>
          </div>
          {eventTypes.slice(0, 3).map(type => (
            <div key={type} className="p-3 text-center">
              <p className="text-lg font-serif font-bold text-accent-navy">{stats.types[type] || 0}</p>
              <p className="text-[9px] font-black uppercase tracking-widest text-text-muted">{ACCESS_EVENT_TYPES[type].label}</p>
            </div>
          ))}
        </div>

        {/* Access alerts availability */}
        <div className="px-5 pt-4">
          <div role="status" className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-2xl">
            <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-amber-900">Access alerts are not active</p>
              <p className="text-[11px] text-amber-800 font-light mt-1">
                This log is stored in this browser only. It cannot reliably report access from other devices or users. Verified access alerts need a server-enforced activity feed, which is not configured.
              </p>
            </div>
          </div>
        </div>

        <DocuVaultAccessAlerts logs={logs} />

        {/* Toolbar */}
        <div className="p-4 border-b border-brand-light flex items-center gap-3 flex-wrap flex-shrink-0 bg-brand-light/30">
          <div className="relative flex-1 min-w-[160px]">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted w-3.5 h-3.5" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search events…"
              className="w-full pl-9 pr-3 py-2 bg-white border-2 border-brand-dark rounded-xl outline-none focus:border-accent-gold text-xs"
            />
          </div>
          <div className="flex gap-1.5 overflow-x-auto pb-1">
            <button
              onClick={() => setFilter('all')}
              className={`flex-shrink-0 px-3 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest border-2 transition-all ${
                filter === 'all' ? 'bg-accent-navy text-white border-accent-navy' : 'border-brand-dark text-text-muted hover:border-accent-navy/40'
              }`}
            >
              All
            </button>
            {eventTypes.map(type => (
              <button
                key={type}
                onClick={() => setFilter(type)}
                className={`flex-shrink-0 px-3 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest border-2 transition-all ${
                  filter === type ? 'bg-accent-navy text-white border-accent-navy' : 'border-brand-dark text-text-muted hover:border-accent-navy/40'
                }`}
              >
                {ACCESS_EVENT_TYPES[type].label}
              </button>
            ))}
          </div>
        </div>

        {/* Timeline */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 bg-brand-light rounded-2xl flex items-center justify-center mb-4">
                <SafeIcon icon={FiList} className="w-8 h-8 text-text-muted/30" />
              </div>
              <p className="text-sm text-text-muted font-light">
                {logs.length === 0 ? 'No access events recorded yet.' : 'No events match your filters.'}
              </p>
            </div>
          ) : (
            <AnimatePresence>
              {filtered.map(log => (
                <LogEntry key={log.id} log={log} showDocName={!doc} />
              ))}
            </AnimatePresence>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-brand-light flex items-center justify-between flex-shrink-0 bg-brand-light/50">
          {showConfirmClear ? (
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold text-red-600">
                Delete all logs{doc ? ' for this document' : ''}?
              </span>
              <button onClick={handleClear} className="px-3 py-1.5 bg-red-500 text-white rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-red-600 transition-all">
                Yes, Clear
              </button>
              <button onClick={() => setShowConfirmClear(false)} className="px-3 py-1.5 border-2 border-brand-dark rounded-xl text-[9px] font-black uppercase tracking-widest text-text-muted hover:text-accent-navy transition-all">
                Cancel
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleExport}
                  disabled={filtered.length === 0}
                  className="flex items-center gap-1.5 px-3 py-2 border-2 border-brand-dark rounded-xl text-[9px] font-black uppercase tracking-widest text-text-muted hover:text-accent-navy hover:border-accent-navy transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <SafeIcon icon={FiSave} className="w-3.5 h-3.5" /> Export CSV
                </button>
                <button
                  onClick={() => setShowConfirmClear(true)}
                  disabled={logs.length === 0}
                  className="flex items-center gap-1.5 px-3 py-2 border-2 border-red-200 rounded-xl text-[9px] font-black uppercase tracking-widest text-red-500 hover:bg-red-50 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" /> Clear
                </button>
              </div>
              <p className="text-[9px] font-black uppercase tracking-widest text-text-muted">
                {filtered.length} of {logs.length} events
              </p>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default DocuVaultAccessLogModal;